/**
 * ReplaceDeviceModal — PROVISIONING-STOCK-QR-6-FRONTEND.
 *
 * Modal de "Substituir equipamento" (troca de Sonoff). Informa o QR físico do
 * novo Sonoff + motivo, exige confirmação e chama o endpoint backend
 * POST /api/admin/devices/bindings/{qr_serial}/replace.
 *
 * NÃO envia comando ao equipamento. Reusa o scanner físico (QrScannerModal) e os
 * helpers de QR físico (só dígitos).
 */
import { useState } from "react";
import QrScannerModal from "./QrScannerModal.jsx";
import {
  isValidPhysicalQr,
  normalizePhysicalQr,
  PHYSICAL_QR_ERROR,
} from "../lib/physicalQr.js";
import {
  replaceErrorMessage,
  replaceSuccessMessage,
} from "../lib/replaceBinding.js";
import { replaceDeviceBinding } from "../lib/api.js";

export default function ReplaceDeviceModal({ binding, onClose, onReplaced }) {
  const [newQr, setNewQr] = useState("");
  const [reason, setReason] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);

  const normalized = normalizePhysicalQr(newQr);
  const sameAsCurrent = normalized && normalized === binding.qr_serial;
  const canSubmit =
    !busy && isValidPhysicalQr(newQr) && reason.trim().length > 0 && !sameAsCurrent;

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await replaceDeviceBinding(binding.qr_serial, {
        new_qr_serial: normalized,
        reason: reason.trim(),
        confirm: true,
      });
      setSuccess(replaceSuccessMessage(!!res?.idempotent));
      setTimeout(() => {
        onReplaced?.();
        onClose();
      }, 1500);
    } catch (e) {
      setErr(replaceErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-noc-bg border border-noc-border rounded-lg p-4 w-full max-w-md space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-bold text-noc-text">Substituir equipamento</h3>

        {/* Equipamento atual */}
        <div className="text-[11px] text-noc-muted">
          QR atual: <span className="font-mono text-noc-text">{binding.qr_serial}</span>
          {binding.device_id && (
            <>
              {" · "}equipamento:{" "}
              <span className="font-mono text-noc-text">{binding.device_id}</span>
            </>
          )}
        </div>

        {/* Aviso de segurança */}
        <div className="text-[11px] text-noc-accent bg-noc-accent/10 border border-noc-accent/30 rounded p-2 leading-relaxed">
          Esta ação <b>não envia comandos</b> ao equipamento antigo. O Sonoff antigo
          será <b>arquivado</b> e um <b>novo vínculo pendente</b> será criado para o
          QR informado.
        </div>

        {success ? (
          <div className="text-[11px] text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded p-2">
            {success}
          </div>
        ) : (
          <>
            {/* QR físico do novo Sonoff */}
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">QR físico do novo Sonoff</span>
              <div className="flex gap-1">
                <input
                  type="text"
                  inputMode="numeric"
                  value={newQr}
                  onChange={(e) => setNewQr(e.target.value)}
                  placeholder="25082200178100"
                  className="flex-1 bg-noc-bg/60 border border-noc-border rounded px-2 py-1 font-mono text-noc-text"
                />
                <button
                  type="button"
                  onClick={() => setScanOpen(true)}
                  className="text-xs px-2 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text"
                >
                  📷 Escanear QR
                </button>
              </div>
            </label>
            {newQr && !isValidPhysicalQr(newQr) && (
              <p className="text-[10px] text-noc-warn">{PHYSICAL_QR_ERROR}</p>
            )}
            {sameAsCurrent && (
              <p className="text-[10px] text-noc-warn">O QR novo é igual ao QR atual.</p>
            )}

            {/* Motivo */}
            <label className="block text-xs space-y-1">
              <span className="text-noc-muted">Motivo da troca</span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex.: defeito no relé / fonte / queima"
                className="w-full bg-noc-bg/60 border border-noc-border rounded px-2 py-1 text-noc-text"
              />
            </label>

            {err && (
              <div className="text-[11px] text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2">
                {err}
              </div>
            )}

            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={onClose}
                className="text-xs px-2.5 py-1 rounded border border-noc-border text-noc-muted hover:text-noc-text"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={!canSubmit}
                className="text-xs px-3 py-1 rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold disabled:opacity-40"
              >
                {busy ? "Substituindo…" : "Confirmar substituição"}
              </button>
            </div>
          </>
        )}
      </div>

      {scanOpen && (
        <QrScannerModal
          onDetected={(value) => {
            setNewQr(normalizePhysicalQr(value));
            setScanOpen(false);
          }}
          onManual={() => setScanOpen(false)}
          onClose={() => setScanOpen(false)}
        />
      )}
    </div>
  );
}
